import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { memo } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { compact } from 'lodash-es';
import { TableListRow, Typography, TableListActions, DropdownMenuItem, ConfirmationModal, WithInfoIcon, Row } from 'seagull';
import { CreditCard, DocumentCheckmark, Download, EmailSent, Eye, LinkIcon, MoneyReceive, Redo, Void } from 'seagull/icons';
import { allowResendAction, allowUnmarkPaidExternallyAction, allowUnvoidAction, allowVoidAction, getStatusLabel, getPaymentDetailTooltip, formatPaymentDetailsList, invoiceLogicTextsPath } from '~/business-logic/invoiceLogic';
import { InvoiceAccountingIntegrationStatus } from '~/components/InvoiceAccountingIntegrationStatus';
import { routes } from '~/constants';
import { useOverlayContext, useStore, useStoreActions } from '~/contexts';
import { useDownloadInvoicePdf } from '~/hooks/invoice/useDownloadInvoicePdf';
import { useCopyDirectLink } from '~/hooks/useCopyDirectLink';
import { usePaidExternally } from '~/hooks/usePaidExternally';
import { useResendInvoice } from '~/hooks/useResendInvoice';
import { useLocaleKeys } from '~/i18n';
import { Intent } from '~/infra/access-control';
import { invoiceSummaryModel, timeModel } from '~/models';
import { selectors } from '~/store/state-selectors';
import { Accounting } from '~/types/api.model';
import styles from './InvoicesTable.module.scss';
const voidLoader = 'void';
const displayedStatusToSkin = {
    issued: undefined,
    pending: undefined,
    overdue: 'error',
    processing: 'success',
    paid: 'success',
    voided: undefined,
    refunded: 'standard',
    refundFailed: 'error',
    disputed: 'error',
    partiallyRefunded: 'standard'
};
function getPaymentMethodText(paymentMethodDisplay, texts) {
    if (paymentMethodDisplay == null) {
        return texts.notSet;
    }
    const typeLabels = {
        ach: texts.ach,
        creditCard: texts.creditCard
    };
    return "".concat(typeLabels[paymentMethodDisplay.type], " •••• ").concat(paymentMethodDisplay.last4);
}
function InvoicesTableRow(props) {
    const { t: texts } = useLocaleKeys('invoice.tableRow');
    const { t: commonTexts } = useLocaleKeys('common');
    const { t: invoiceTexts } = useLocaleKeys('invoice');
    const { t: invoiceLogicTexts } = useLocaleKeys(invoiceLogicTextsPath);
    const { invoice, hideDueDate, showQboIntegration, showXeroIntegration, AD_HOC_INVOICING, to, retry, onUnvoidInvoice, onUnmarkPaidExternally } = props;
    const overlayContext = useOverlayContext();
    const { markAsPaid, shouldShowMarkAsPaid } = usePaidExternally();
    const { resendInvoice } = useResendInvoice();
    const history = useHistory();
    const isVendor = useStore(selectors.user.isVendor);
    const email = useStore(selectors.user.email);
    const testId = "invoice-row";
    const { api, logBI } = useStoreActions();
    const { copyDirectLink } = useCopyDirectLink();
    const { downloadInvoicePdf } = useDownloadInvoicePdf();
    const isAdHoc = AD_HOC_INVOICING && invoice.isAdHoc;
    const paymentDetailTooltip = getPaymentDetailTooltip(invoice.paymentDetails, invoiceLogicTexts.paymentDetailTooltips);
    const onVoidInvoice = ()=>{
        overlayContext.showModal({
            biName: 'invoices_table_row_void_confirm',
            closeOnClickOutside: true,
            closeOnEscapeKey: true,
            openBottomSheetOnMobile: true,
            hideBottomSheetOnDismiss: true,
            content: (param)=>{
                let { dismissModal } = param;
                return /*#__PURE__*/ _jsx(ConfirmationModal, {
                    bi: {
                        primaryButton: {
                            name: 'invoices_table_row_confirm'
                        },
                        secondaryButton: {
                            name: 'invoices_table_row_cancel'
                        }
                    },
                    title: texts.voidTitle(),
                    content: texts.voidConfirmation({
                        invoiceNumber: invoice.formattedNumber
                    }),
                    testId: "void-confirmation-modal",
                    primaryButtonLabel: commonTexts.buttons.imSure(),
                    secondaryButtonLabel: commonTexts.buttons.cancel(),
                    onSecondaryButtonClick: ()=>dismissModal(),
                    onPrimaryButtonClick: async ()=>{
                        try {
                            overlayContext.startLoader(voidLoader);
                            await api.invoice.void(invoice.id);
                            await retry();
                            dismissModal(true);
                        } catch (error) {
                            overlayContext.showErrorToast(commonTexts.toasts.unableToVoidInvoice(), 'void-invoice-error');
                            dismissModal();
                        } finally{
                            overlayContext.stopLoader(voidLoader);
                        }
                    }
                });
            }
        });
    };
    return /*#__PURE__*/ _jsx(TableListRow, {
        as: Link,
        to: to,
        size: "large",
        testId: testId,
        "data-heapid": "invoice",
        items: compact([
            {
                className: styles.invoiceIdCell,
                value: /*#__PURE__*/ _jsxs(Row, {
                    alignItems: "center",
                    gap: 8,
                    children: [
                        /*#__PURE__*/ _jsx(Typography, {
                            testId: "".concat(testId, "-invoice-no"),
                            skin: "secondary",
                            className: styles.invoiceId,
                            children: invoice.formattedNumber
                        }),
                        showQboIntegration && /*#__PURE__*/ _jsx(InvoiceAccountingIntegrationStatus, {
                            testId: "".concat(testId, "-qbo"),
                            invoiceIntegration: invoice.integration,
                            accountingType: Accounting.QBO
                        }),
                        showXeroIntegration && /*#__PURE__*/ _jsx(InvoiceAccountingIntegrationStatus, {
                            testId: "".concat(testId, "-xero"),
                            invoiceIntegration: invoice.xeroIntegration.getIntegration(),
                            accountingType: Accounting.Xero
                        })
                    ]
                })
            },
            {
                value: /*#__PURE__*/ _jsxs(Row, {
                    alignItems: "center",
                    gap: 4,
                    children: [
                        /*#__PURE__*/ _jsxs(Typography, {
                            ellipsis: true,
                            weight: "normal",
                            testId: "".concat(testId, "-comp-name"),
                            children: [
                                invoiceSummaryModel.otherPartyCompanyName(invoice, isVendor),
                                "\xa0"
                            ]
                        }),
                        isAdHoc && /*#__PURE__*/ _jsx(WithInfoIcon, {
                            testId: "".concat(testId, "-ad-hoc-info"),
                            infoTestId: "".concat(testId, "-ad-hoc-info-icon"),
                            info: invoiceTexts.adHocTooltip(),
                            showInfo: true
                        })
                    ]
                })
            },
            {
                value: /*#__PURE__*/ _jsx(Typography, {
                    skin: displayedStatusToSkin[invoiceSummaryModel.displayStatus(invoice)],
                    weight: "normal",
                    testId: "".concat(testId, "-status"),
                    children: getStatusLabel(invoiceSummaryModel.displayStatus(invoice), invoiceLogicTexts.displayedStatusLabels)
                })
            },
            {
                value: invoice.paymentDetails.length > 0 && /*#__PURE__*/ _jsx(WithInfoIcon, {
                    className: styles.paymentDetailsCell,
                    info: paymentDetailTooltip,
                    showInfo: !!paymentDetailTooltip,
                    children: /*#__PURE__*/ _jsx(Typography, {
                        ellipsis: true,
                        testId: "".concat(testId, "-payment-details"),
                        children: formatPaymentDetailsList(invoice.paymentDetails, invoiceLogicTexts.paymentDetailLabels, invoiceLogicTexts.labelOnDate)
                    })
                })
            },
            AD_HOC_INVOICING && {
                value: /*#__PURE__*/ _jsx(Typography, {
                    skin: invoice.paymentMethodDisplay != null ? undefined : 'secondary',
                    testId: "".concat(testId, "-payment-method"),
                    ellipsis: true,
                    children: getPaymentMethodText(invoice.paymentMethodDisplay, {
                        notSet: invoiceTexts.paymentMethodDisplay.notSet(),
                        ach: invoiceTexts.paymentMethodDisplay.ach(),
                        creditCard: invoiceTexts.paymentMethodDisplay.creditCard()
                    })
                })
            },
            {
                value: /*#__PURE__*/ _jsx(Typography, {
                    skin: "secondary",
                    testId: "".concat(testId, "-issued-on"),
                    children: timeModel.formatDateLong(invoice.rawIssuedOn.getTime())
                })
            },
            !hideDueDate && {
                value: /*#__PURE__*/ _jsx(Typography, {
                    skin: "secondary",
                    testId: "".concat(testId, "-due-date"),
                    children: timeModel.formatDateLong(invoice.rawDueDate.getTime())
                })
            },
            {
                value: /*#__PURE__*/ _jsx(Typography, {
                    weight: "light",
                    skin: "secondary",
                    testId: "".concat(testId, "-amount"),
                    ellipsis: true,
                    children: invoice.totalAmount
                })
            }
        ]),
        actionColumn: /*#__PURE__*/ _jsxs(TableListActions, {
            buttonText: texts.view(),
            buttonIcon: /*#__PURE__*/ _jsx(Eye, {}),
            onClickButton: ()=>history.push(to),
            testId: testId,
            btnHeapId: "invoice-view-btn",
            menuHeapId: "invoice-menu-btn",
            mobile: {
                title: invoice.formattedNumber,
                subtitle: invoiceSummaryModel.otherPartyCompanyName(invoice, isVendor)
            },
            children: [
                shouldShowMarkAsPaid(invoiceSummaryModel.displayStatus(invoice), invoiceSummaryModel.paymentDetails(invoice), isVendor) && /*#__PURE__*/ _jsx(DropdownMenuItem, {
                    intent: Intent.InvoiceMarkPaidExternally,
                    testId: "".concat(testId, "-paid-externally"),
                    "data-heapid": "invoice-menu-paid-externally",
                    iconStart: /*#__PURE__*/ _jsx(MoneyReceive, {}),
                    onClick: ()=>markAsPaid(invoice, retry),
                    bi: {
                        name: 'mark_paid_externally'
                    },
                    children: commonTexts.buttons.markAsPaidExternally()
                }),
                allowResendAction(invoiceSummaryModel.displayStatus(invoice), isVendor) && /*#__PURE__*/ _jsx(DropdownMenuItem, {
                    intent: Intent.InvoiceResend,
                    testId: "".concat(testId, "-resend-invoice"),
                    "data-heapid": "invoice-menu-resend-invoice",
                    iconStart: /*#__PURE__*/ _jsx(EmailSent, {}),
                    onClick: ()=>{
                        resendInvoice(invoice).then(retry);
                    },
                    bi: {
                        name: 'resend_invoice'
                    },
                    children: commonTexts.buttons.resendInvoice()
                }),
                /*#__PURE__*/ _jsx(DropdownMenuItem, {
                    intent: Intent.InvoiceDownload,
                    testId: "".concat(testId, "-download-invoice"),
                    "data-heapid": "invoice-menu-download-invoice",
                    iconStart: /*#__PURE__*/ _jsx(Download, {}),
                    onClick: ()=>downloadInvoicePdf(invoice.id),
                    disabled: !invoice.pdfExists,
                    tooltipProps: invoice.pdfExists ? undefined : {
                        title: invoiceTexts.generatingPdf()
                    },
                    bi: {
                        name: 'download_invoice'
                    },
                    children: commonTexts.buttons.downloadInvoice()
                }),
                /*#__PURE__*/ _jsx(DropdownMenuItem, {
                    testId: "".concat(testId, "-agreement"),
                    "data-heapid": "invoice-menu-agreement",
                    as: isAdHoc ? undefined : Link,
                    to: isAdHoc ? undefined : routes.agreement(invoiceSummaryModel.relationshipId(invoice)),
                    disabled: isAdHoc,
                    tooltipProps: isAdHoc ? {
                        title: invoiceTexts.adHocTooltip()
                    } : undefined,
                    iconStart: /*#__PURE__*/ _jsx(DocumentCheckmark, {}),
                    bi: {
                        name: 'view_agreement'
                    },
                    children: commonTexts.buttons.viewAgreement()
                }),
                AD_HOC_INVOICING && /*#__PURE__*/ _jsx(DropdownMenuItem, {
                    testId: "".concat(testId, "-manage-payment-methods"),
                    "data-heapid": "invoice-menu-manage-payment-methods",
                    as: Link,
                    to: routes.paymentMethods(invoiceSummaryModel.relationshipId(invoice)),
                    iconStart: /*#__PURE__*/ _jsx(CreditCard, {}),
                    bi: {
                        name: 'manage_payment_methods'
                    },
                    children: invoiceTexts.managePaymentMethods()
                }),
                allowVoidAction(invoiceSummaryModel.displayStatus(invoice), invoiceSummaryModel.paymentDetails(invoice), isVendor) && /*#__PURE__*/ _jsx(DropdownMenuItem, {
                    intent: Intent.InvoiceVoid,
                    iconStart: /*#__PURE__*/ _jsx(Void, {}),
                    onClick: onVoidInvoice,
                    testId: "".concat(testId, "-void"),
                    "data-heapid": "invoice-menu-void",
                    bi: {
                        name: 'void_invoice'
                    },
                    children: commonTexts.buttons.voidInvoice()
                }),
                allowUnvoidAction(invoice, isVendor) && /*#__PURE__*/ _jsx(DropdownMenuItem, {
                    intent: Intent.InvoiceUnvoid,
                    iconStart: /*#__PURE__*/ _jsx(Redo, {}),
                    onClick: onUnvoidInvoice,
                    testId: "".concat(testId, "-unvoid"),
                    "data-heapid": "invoice-menu-unvoid",
                    bi: {
                        name: 'unvoid_invoice'
                    },
                    children: commonTexts.buttons.unvoidInvoice()
                }),
                allowUnmarkPaidExternallyAction(invoice, isVendor) && /*#__PURE__*/ _jsx(DropdownMenuItem, {
                    intent: Intent.InvoiceUnmarkPaidExternally,
                    iconStart: /*#__PURE__*/ _jsx(Redo, {}),
                    onClick: onUnmarkPaidExternally,
                    testId: "".concat(testId, "-unmark-paid-externally"),
                    "data-heapid": "invoice-menu-unmark-paid-externally",
                    bi: {
                        name: 'unmark_paid_externally'
                    },
                    children: commonTexts.buttons.unmarkAsPaidExternally()
                }),
                /*#__PURE__*/ _jsx(DropdownMenuItem, {
                    intent: Intent.InvoiceCopyLink,
                    testId: "".concat(testId, "-copy-link"),
                    "data-heapid": "invoice-menu-copy-link",
                    iconStart: /*#__PURE__*/ _jsx(LinkIcon, {}),
                    bi: {
                        name: 'copy_link'
                    },
                    onClick: ()=>{
                        if (isVendor) {
                            logBI(14001, {
                                email
                            });
                        } else {
                            logBI(14003);
                        }
                        copyDirectLink(invoiceSummaryModel.relationshipId(invoice), {
                            type: 'invoice',
                            id: invoice.id
                        });
                    },
                    children: commonTexts.buttons.copyLink()
                })
            ]
        })
    }, invoice.id);
}
export default /*#__PURE__*/ memo(InvoicesTableRow);
